import React from 'react';

import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import StorageIcon from '@mui/icons-material/Storage';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import DnsIcon from '@mui/icons-material/Dns';
// import ComputerIcon from '@mui/icons-material/Computer';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import CachedIcon from '@mui/icons-material/Cached';
import CloudCircleIcon from '@mui/icons-material/CloudCircle';
import DescriptionIcon from '@mui/icons-material/Description';

import { FormatBytesM, FormatBits } from './format';
import { FetchAPI } from './auth-service';
import { get } from 'lodash';

class HealthDataService {
    constructor () {
        if (!HealthDataService.instance) {
            HealthDataService.instance = this;
        }

        const MC2Check = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

        this.getSystemHealth = (uuid) => {
            return FetchAPI({
                method: 'GET',
                url: MC2Check && uuid
                    ? `mc2/cmc/${uuid}/`
                    : 'system/health',
            });
        };

        this.getHealthHistory = (uuid, cmc) => {
            return FetchAPI({
                method: 'GET',
                url: cmc ? `mc2/sensors/${cmc}/${uuid}/health/history/`
                    : MC2Check
                        ? `mc2/cmc/${uuid}/health/history/`
                        : `sensors/${uuid}/health/history`,
            });
        };

        this.getSensorHistoryMetrics = (btsensor) => {
            const sensorMetrics = [
                {
                    name: 'issues',
                    label: 'Health History',
                    icon: <LocalHospitalIcon />,
                    metrics: [
                        {
                            key: 'issue_count',
                            type: 'bar',
                            title: 'Issues',
                            minY: 0,
                            nilValue: 1,
                            value: (d) => {
                                const count = get(d, 'issues.issue_count');
                                return count === 0 ? 1 : count;
                            },
                            bad: (d) => {
                                return d.issues ? d.issues.issue_count > 0 : false;
                            },
                            good: (d) => {
                                return d.issues ? d.issues.issue_count < 1 : false;
                            },
                            tipFormat: (v, d) => {
                                if (d.issues.issue_count > 0) {
                                    const msg = d.issues.issue_msg
                                        ? d.issues.issue_msg.split(',').map(s => [s.trim(), ''])
                                        : 'Sensor health issue' + (d.issues.issue_count > 1 ? 's' : '');
                                    return msg;
                                } else return 'No issues reported';
                            }
                        }
                    ]
                },
                {
                    name: 'packets',
                    label: 'Network Traffic',
                    icon: <LocalHospitalIcon />,
                    metrics: [
                        {
                            key: 'avg_rx_bytes_per_sec',
                            type: 'line',
                            minY: 0,
                            title: 'RX Bits',
                            value: d => get(d, 'packets.avg_rx_bytes_per_sec') * 8,
                            tipFormat: (v, d) => TxBitsTipFilter(v)
                        },
                        {
                            key: 'avg_rx_drops_per_sec',
                            type: 'line',
                            minY: 0,
                            title: 'RX Drops',
                            value: d => get(d, 'packets.avg_rx_drops_per_sec'),
                            tipFormat: (v, d) => TxPktsTipFilter(v)
                        },
                        {
                            key: 'avg_rx_errs_per_sec',
                            type: 'line',
                            minY: 0,
                            title: 'RX Errs',
                            value: d => get(d, 'packets.avg_rx_errs_per_sec'),
                            tipFormat: (v, d) => TxPktsTipFilter(v)
                        },
                        {
                            key: 'avg_rx_pkts_per_sec',
                            type: 'line',
                            minY: 0,
                            title: 'RX Pkts',
                            value: d => get(d, 'packets.avg_rx_pkts_per_sec'),
                            tipFormat: (v, d) => TxPktsTipFilter(v)
                        },
                        {
                            key: 'avg_tx_bytes_per_sec',
                            type: 'line',
                            minY: 0,
                            title: 'Tx Bits',
                            value: d => get(d, 'packets.avg_tx_bytes_per_sec') * 8,
                            tipFormat: (v, d) => TxBitsTipFilter(v)
                        },
                        {
                            key: 'avg_tx_drops_per_sec',
                            type: 'line',
                            minY: 0,
                            title: 'Tx Drops',
                            value: d => get(d, 'packets.avg_tx_drops_per_sec'),
                            tipFormat: (v, d) => TxPktsTipFilter(v)
                        },
                        {
                            key: 'avg_tx_errs_per_sec',
                            type: 'line',
                            minY: 0,
                            title: 'Tx Errs',
                            value: d => get(d, 'packets.avg_tx_errs_per_sec'),
                            tipFormat: (v, d) => TxPktsTipFilter(v)
                        },
                        {
                            key: 'avg_tx_pkts_per_sec',
                            type: 'line',
                            minY: 0,
                            title: 'Tx Pkts',
                            value: d => get(d, 'packets.avg_tx_pkts_per_sec'),
                            tipFormat: (v, d) => TxPktsTipFilter(v)
                        },
                    ],
                },
                {
                    name: 'eventd',
                    label: 'Events',
                    icon: <LocalHospitalIcon />,
                    metrics: [
                        {
                            key: 'avg_alerts_cmc_per_min',
                            type: 'line',
                            minY: 0,
                            title: 'Alerts (All)',
                            value: d => get(d, 'eventd.avg_alerts_cmc_per_min'),
                            tipFormat: (v, d) => alertsTipFilter(v),
                        },
                        {
                            key: 'avg_alert_action_allowed_per_min',
                            type: 'line',
                            minY: 0,
                            title: 'Alerts (Allowed)',
                            value: d => get(d, 'eventd.avg_alert_action_allowed_per_min'),
                            tipFormat: (v, d) => alertsTipFilter(v),
                        },
                        {
                            key: 'avg_alert_action_blocked_per_min',
                            type: 'line',
                            minY: 0,
                            title: 'Alerts (Blocked)',
                            value: d => get(d, 'eventd.avg_alert_action_blocked_per_min'),
                            tipFormat: (v, d) => alertsTipFilter(v),
                        },
                        {
                            key: 'avg_alert_action_other_per_min',
                            type: 'line',
                            minY: 0,
                            title: 'Alerts (Other)',
                            value: d => get(d, 'eventd.avg_alert_action_other_per_min'),
                            tipFormat: (v, d) => alertsTipFilter(v),
                        },
                        {
                            key: 'avg_bytes_cmc_per_sec',
                            type: 'line',
                            minY: 0,
                            title: 'Alerts Bytes',
                            value: d => get(d, 'eventd.avg_bytes_cmc_per_sec'),
                            tipFormat: (v, d) => BytesTipFilter(v),
                        },
                        {
                            key: 'avg_throttled_alerts_per_min',
                            type: 'line',
                            minY: 0,
                            title: 'Throttled Alerts',
                            value: d => get(d, 'eventd.avg_throttled_alerts_per_min'),
                            tipFormat: (v, d) => alertsTipFilter(v),
                        },
                        {
                            key: 'avg_queue_size',
                            type: 'line',
                            minY: 0,
                            title: 'Alerts Queue Size',
                            value: d => get(d, 'eventd.avg_queue_size'),
                            tipFormat: (v, d) => Alerts2TipFilter(v),
                        },
                        {
                            key: 'avg_metadata_jsons_per_sec',
                            type: 'line',
                            minY: 0,
                            title: 'Metadata',
                            value: d => get(d, 'eventd.avg_metadata_jsons_per_sec'),
                            tipFormat: (v, d) => logsTipFilter(v),
                        },
                        {
                            key: 'avg_metadata_bytes_per_sec',
                            type: 'line',
                            minY: 0,
                            title: 'Metadata Bytes',
                            value: d => get(d, 'eventd.avg_metadata_bytes_per_sec'),
                            tipFormat: (v, d) => BytesTipFilter(v),
                        },
                        {
                            key: 'avg_metadatr_queue_size',
                            type: 'line',
                            minY: 0,
                            title: 'Metadata Queue Size',
                            value: d => get(d, 'eventd.avg_metadatr_queue_size'),
                            tipFormat: (v, d) => Logs2TipFilter(v),
                        },
                    ],
                },
                {
                    name: 'eexportd',
                    label: 'Exports',
                    icon: <LocalHospitalIcon />,
                    metrics: [
                        {
                            key: 'avg_alerts_export_per_min',
                            type: 'line',
                            title: 'Alerts',
                            subtitle: 'avg p/m',
                            value: d => get(d, 'eexportd.avg_alerts_export_per_min'),
                            tipFormat: (v, d) => alertsTipFilter(v)
                        },
                        {
                            key: 'avg_bro_logs_export_per_sec',
                            type: 'line',
                            title: 'Metadata',
                            subtitle: 'avg p/s',
                            value: d => get(d, 'eexportd.avg_bro_logs_export_per_sec'),
                            tipFormat: (v, d) => logsTipFilter(v)
                        },
                        {
                            key: 'avg_bytes_export_per_sec',
                            type: 'line',
                            title: 'Bytes',
                            subtitle: 'avg p/s',
                            value: d => get(d, 'eexportd.avg_bytes_export_per_sec'),
                            tipFormat: (v, d) => BytesTipFilter(v)
                        }
                    ],
                },
                {
                    name: 'captured',
                    label: 'PCAP',
                    icon: <LocalHospitalIcon />,
                    metrics: [
                        {
                            key: 'avg_pkts_per_sec',
                            type: 'line',
                            minY: 0,
                            title: 'Packets',
                            value: d => get(d, 'captured.avg_pkts_per_sec'),
                            tipFormat: (v, d) => TxPktsTipFilter(v),
                        },
                        {
                            key: 'avg_bytes_per_sec',
                            type: 'line',
                            minY: 0,
                            title: 'Bytes',
                            value: d => get(d, 'captured.avg_bytes_per_sec'),
                            tipFormat: (v, d) => BytesTipFilter(v),
                        },
                    ],
                },
                {
                    name: 'mwd',
                    label: 'Malware',
                    icon: <LocalHospitalIcon />,
                    metrics: [
                        {
                            key: 'avg_cyl_hit_per_min',
                            type: 'line',
                            minY: 0,
                            title: 'Cylance Alerts',
                            value: d => get(d, 'mwd.avg_cyl_hit_per_min'),
                            tipFormat: (v, d) => alertsTipFilter(v),
                        },
                        {
                            key: 'avg_mls_hit_per_min',
                            type: 'line',
                            minY: 0,
                            title: 'ML Server Alerts',
                            value: d => get(d, 'mwd.avg_mls_hit_per_min'),
                            tipFormat: (v, d) => alertsTipFilter(v),
                        },
                        {
                            key: 'avg_cyl_input_file_per_min',
                            type: 'line',
                            minY: 0,
                            title: 'Analyzed Files',
                            value: d => get(d, 'mwd.avg_cyl_input_file_per_min'),
                            tipFormat: (v, d) => FilesTipFilter(v),
                        },
                    ],
                },
                {
                    name: 'system',
                    label: 'System',
                    icon: <LocalHospitalIcon />,
                    metrics: [
                        {
                            key: 'avg_used_cpu_pct',
                            type: 'line',
                            minY: 0,
                            title: 'Avg. CPU Usage',
                            value: d => get(d, 'system.avg_used_cpu_pct'),
                            tipFormat: (v, d) => usedCpu(v)
                        },
                        {
                            key: 'avg_avail_mem_mb',
                            type: 'line',
                            minY: 0,
                            title: 'Avg. Avail. Mem',
                            value: d => get(d, 'system.avg_avail_mem_mb'),
                            tipFormat: (v, d) => availMem(v)
                        },
                        {
                            key: 'avg_load_1m',
                            type: 'line',
                            minY: 0,
                            title: 'Load Avg. 1 min.',
                            value: d => get(d, 'system.avg_load_1m'),
                            tipFormat: (v, d) => loadAvg(v)
                        },
                        {
                            key: 'avg_load_5m',
                            type: 'line',
                            minY: 0,
                            title: 'Load Avg. 5 min.',
                            value: d => get(d, 'system.avg_load_5m'),
                            tipFormat: (v, d) => loadAvg(v)
                        },
                        {
                            key: 'avg_load_15m',
                            type: 'line',
                            minY: 0,
                            title: 'Load Avg. 15 min.',
                            value: d => get(d, 'system.avg_load_15m'),
                            tipFormat: (v, d) => loadAvg(v)
                        }
                    ],
                },
                {
                    name: 'disks',
                    label: 'Disks',
                    icon: <LocalHospitalIcon />,
                    nested: true,
                    metrics: [
                        {
                            key: 'avg_utilization',
                            type: 'line',
                            title: 'Utilization',
                            minY: 0,
                            maxY: 1,
                            tipFormat: (v, d) => percentFilter(v)
                        },
                        {
                            key: 'avg_inode_utilization',
                            type: 'line',
                            title: 'Inode Util.',
                            minY: 0,
                            maxY: 1,
                            tipFormat: (v, d) => percentFilter(v)
                        }
                    ],
                },
                {
                    name: 'apps',
                    label: 'Applications',
                    icon: <LocalHospitalIcon />,
                    nested: true,
                    metrics: [
                        {
                            key: 'avg_pct_cpu',
                            type: 'line',
                            title: 'CPU%',
                            minY: 0,
                            maxY: 100,
                            value: () => {},
                            tipFormat: (v, d) => usedCpu(v)
                        },
                        {
                            key: 'avg_rss',
                            type: 'line',
                            title: 'RSS',
                            value: () => {},
                            tipFormat: (v, d) => kBytesTipFilter(v)
                        },
                        {
                            key: 'avg_procs',
                            minY: 0,
                            type: 'line',
                            title: 'Procs'
                        },
                        {
                            key: 'avg_threads',
                            minY: 0,
                            type: 'line',
                            title: 'Threads'
                        },
                        {
                            key: 'avg_files',
                            minY: 0,
                            type: 'line',
                            title: 'Files'
                        }
                    ],
                },
            ];

            // if backtesting sensor, can hide the network tab
            return sensorMetrics.filter(m => !btsensor || m.name !== 'packets');
        };

        // cmc health metrics
        this.getHistoryMetrics = (sensorNames) => {
            return [{
                name: 'issues',
                label: 'Health History',
                icon: <LocalHospitalIcon/>,
                metrics: [
                    {
                        key: 'issue_count',
                        type: 'bar',
                        title: 'Issues',
                        minY: 0,
                        nilValue: 1,
                        value: function (d) {
                        // show a bar regardless
                            const count = get(d, 'issues.issue_count');
                            return count === 0 ? 1 : count;
                        },
                        bad: function (d) {
                            return d.issues ? d.issues.issue_count > 0 : false;
                        },
                        good: function (d) {
                            return d.issues ? d.issues.issue_count < 1 : false;
                        },
                        tipFormat: function (v, d) {
                            if (d.issues.issue_count > 0) {
                                const msg = d.issues.issue_msg
                                    ? d.issues.issue_msg.split(',').map(s => [s.trim(), ''])
                                    : 'CMC health issue' + (d.issues.issue_count > 1 ? 's' : '');
                                return msg;
                            }
                            return 'no issues reported';
                        }
                    }]
            },
            {
                name: 'license',
                label: 'License',
                icon: <DescriptionIcon/>,
                metrics: [
                    {
                        key: 'avg_rx_bytes_per_sec',
                        type: 'line',
                        minY: 0,
                        title: 'Total sensors RX Bits',
                        value: d => get(d, 'license.total_rx_bytes_per_sec') * 8,
                        tipFormat: (v, d) => TxBitsSnsrsTipFilter(v, get(d, 'license.sensor_count')),
                    },
                    // {
                    //     key: 'max_snsrs_num_cores',
                    //     type: 'bar',
                    //     minY: 0,
                    //     title: 'Max Cores p/Sensor',
                    //     value: d => get(d, 'license.max_snsrs_num_cores'),
                    //     tipFormat: (v, d) => `${v} cores on '${sensorName(d, sensorNames)}'`
                    // },
                    // {
                    //     key: 'total_num_cores',
                    //     type: 'bar',
                    //     minY: 0,
                    //     title: 'Total Num Cores',
                    //     value: d => get(d, 'license.total_num_cores'),
                    //     tipFormat: (v, d) => `${v} total cores for ${get(d, 'license.sensor_count')} sensor${get(d, 'license.sensor_count', 0) > 1 ? 's' : ''}`
                    // },
                ]
            },
            {
                name: 'system',
                label: 'System',
                icon: <DnsIcon/>,
                metrics: [
                    {
                        key: 'avg_used_cpu_pct',
                        type: 'line',
                        minY: 0,
                        title: 'Avg. CPU Usage',
                        value: d => get(d, 'system.avg_used_cpu_pct'),
                        tipFormat: (v, d) => usedCpu(v)
                    },
                    {
                        key: 'avg_avail_mem_mb',
                        type: 'line',
                        minY: 0,
                        title: 'Avg. Avail. Mem',
                        value: d => get(d, 'system.avg_avail_mem_mb'),
                        tipFormat: (v, d) => availMem(v)
                    },
                    {
                        key: 'avg_load_1m',
                        type: 'line',
                        minY: 0,
                        title: 'Load Avg. 1 min.',
                        value: d => get(d, 'system.avg_load_1m'),
                        tipFormat: (v, d) => loadAvg(v)
                    },
                    {
                        key: 'avg_load_5m',
                        type: 'line',
                        minY: 0,
                        title: 'Load Avg. 5 min.',
                        value: d => get(d, 'system.avg_load_5m'),
                        tipFormat: (v, d) => loadAvg(v)
                    },
                    {
                        key: 'avg_load_15m',
                        type: 'line',
                        minY: 0,
                        title: 'Load Avg. 15 min.',
                        value: d => get(d, 'system.avg_load_15m'),
                        tipFormat: (v, d) => loadAvg(v)
                    }
                ]
            },
            {
                name: 'eexportd',
                label: 'Exports',
                icon: <ImportExportIcon/>,
                metrics: [
                    {
                        key: 'avg_alerts_export_per_min',
                        type: 'line',
                        title: 'Alerts',
                        subtitle: 'avg p/m',
                        value: d => get(d, 'eexportd.avg_alerts_export_per_min'),
                        tipFormat: (v, d) => alertsTipFilter(v)
                    },
                    {
                        key: 'avg_bro_logs_export_per_sec',
                        type: 'line',
                        title: 'Metadata',
                        subtitle: 'avg p/s',
                        value: d => get(d, 'eexportd.avg_bro_logs_export_per_sec'),
                        tipFormat: (v, d) => logsTipFilter(v)
                    },
                    {
                        key: 'avg_bytes_export_per_sec',
                        type: 'line',
                        title: 'Bytes',
                        subtitle: 'avg p/s',
                        value: d => get(d, 'eexportd.avg_bytes_export_per_sec'),
                        tipFormat: (v, d) => BytesTipFilter(v)
                    }
                ]
            },
            {
                name: 'dbwd',
                label: 'DB',
                icon: <StorageIcon/>,
                metrics: [
                    {
                        key: 'avg_alerts_rcv_per_min',
                        type: 'line',
                        title: 'Alerts',
                        subtitle: 'avg p/m',
                        value: d => get(d, 'dbwd.avg_alerts_rcv_per_min'),
                        tipFormat: (v, d) => alertsTipFilter(v)
                    },
                    {
                        key: 'avg_bytes_rcv_per_sec',
                        type: 'line',
                        title: 'Rcv Bytes',
                        subtitle: 'avg p/s',
                        value: d => get(d, 'dbwd.avg_bytes_rcv_per_sec'),
                        tipFormat: (v, d) => BytesTipFilter(v)
                    },
                    {
                        key: 'avg_bytes_db_per_sec',
                        type: 'line',
                        title: 'DB Bytes',
                        subtitle: 'avg p/s',
                        value: d => get(d, 'dbwd.avg_bytes_db_per_sec'),
                        tipFormat: (v, d) => BytesTipFilter(v)
                    },
                    {
                        key: 'db_fill',
                        type: 'line',
                        title: 'Fill',
                        minY: 0,
                        maxY: 1,
                        value: d => get(d, 'dbwd.db_fill'),
                        tipFormat: (v, d) => percentFilter(v)
                    }
                ]
            },
            {
                name: 'eswd',
                label: 'Opensearch',
                icon: <StorageIcon />,
                metrics: [
                    {
                        key: 'avg_bro_logs_rcv_per_sec',
                        type: 'line',
                        title: 'Metadata',
                        subtitle: 'avg p/s',
                        value: d => get(d, 'eswd.avg_bro_logs_rcv_per_sec'),
                        tipFormat: (v, d) => logsTipFilter(v)
                    },
                    {
                        key: 'avg_bytes_rcv_per_sec',
                        type: 'line',
                        title: 'Rcv Bytes',
                        subtitle: 'avg p/s',
                        value: d => get(d, 'eswd.avg_bytes_rcv_per_sec'),
                        tipFormat: (v, d) => BytesTipFilter(v)
                    },
                    {
                        key: 'avg_es_recs_per_sec',
                        type: 'line',
                        title: 'Opensearch Recs',
                        subtitle: 'avg p/s',
                        value: d => get(d, 'eswd.avg_es_recs_per_sec'),
                        tipFormat: (v, d) => logsTipFilter(v)
                    },
                    {
                        key: 'avg_bytes_es_per_sec',
                        type: 'line',
                        title: 'Opensearch Bytes',
                        subtitle: 'avg p/s',
                        value: d => get(d, 'eswd.avg_bytes_es_per_sec'),
                        tipFormat: (v, d) => BytesTipFilter(v)
                    },
                ]
            },
            {
                name: 'disks',
                label: 'Disks',
                icon: <ViewModuleIcon/>,
                nested: true,
                metrics: [
                    {
                        key: 'avg_utilization',
                        type: 'line',
                        title: 'Utilization',
                        minY: 0,
                        maxY: 1,
                        tipFormat: (v, d) => percentFilter(v)
                    }
                ]
            },
            {
                name: 'apps',
                label: 'Applications',
                icon: <CachedIcon/>,
                nested: true,
                metrics: [
                    {
                        key: 'avg_pct_cpu',
                        type: 'line',
                        title: 'CPU%',
                        minY: 0,
                        maxY: 100,
                        value: () => {},
                        tipFormat: (v, d) => usedCpu(v)
                    },
                    {
                        key: 'avg_rss',
                        type: 'line',
                        title: 'RSS',
                        value: () => {},
                        tipFormat: (v, d) => kBytesTipFilter(v)
                    },
                    {
                        key: 'avg_procs',
                        minY: 0,
                        type: 'line',
                        title: 'Procs'
                    },
                    {
                        key: 'avg_threads',
                        minY: 0,
                        type: 'line',
                        title: 'Threads'
                    },
                    {
                        key: 'avg_files',
                        minY: 0,
                        type: 'line',
                        title: 'Files'
                    }
                ]
            },
            {
                name: 'aws_stats',
                label: 'AWS Stats',
                icon: <CloudCircleIcon/>,
                metrics: [
                    {
                        key: 'avg_used_cpu_pct',
                        type: 'line',
                        title: 'Avg. CPU Usage',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_used_cpu_pct'),
                        tipFormat: (v, d) => usedCpu(v)
                    },
                    {
                        key: 'avg_disk_read_ops',
                        type: 'line',
                        title: 'Avg. Disk Read Ops',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_disk_read_ops'),
                        tipFormat: (v, d) => opsTipFilter(v)
                    },
                    {
                        key: 'avg_disk_write_ops',
                        type: 'line',
                        title: 'Avg. Disk Write Ops',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_disk_write_ops'),
                        tipFormat: (v, d) => opsTipFilter(v)
                    },
                    {
                        key: 'avg_disk_read_bytes',
                        type: 'line',
                        title: 'Avg. Disk Read Bytes',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_disk_read_bytes'),
                        tipFormat: (v, d) => BytesTipFilter(v)
                    },
                    {
                        key: 'avg_disk_write_bytes',
                        type: 'line',
                        title: 'Avg. Disk Write Bytes',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_disk_write_bytes'),
                        tipFormat: (v, d) => BytesTipFilter(v)
                    },
                    {
                        key: 'avg_nw_in_bytes',
                        type: 'line',
                        title: 'Avg. Network In Bytes',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_nw_in_bytes'),
                        tipFormat: (v, d) => BytesTipFilter(v)
                    },
                    {
                        key: 'avg_nw_out_bytes',
                        type: 'line',
                        title: 'Avg. Network Out Bytes',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_nw_out_bytes'),
                        tipFormat: (v, d) => BytesTipFilter(v)
                    },
                    {
                        key: 'avg_nw_pkts_in',
                        type: 'line',
                        title: 'Avg. Network Packets In',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_nw_pkts_in'),
                        tipFormat: (v, d) => TxBitsTipFilter(v)
                    },
                    {
                        key: 'avg_nw_pkts_out',
                        type: 'line',
                        title: 'Avg. Network Packets Out',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_nw_pkts_out'),
                        tipFormat: (v, d) => TxBitsTipFilter(v)
                    }
                ]
            }];
        };

        return HealthDataService.instance;
    }
}

// converts Bytes/s to most reasonable bps unit up to Gbps
// returns string with unit
function TxBytesTipFilter (value) { return FormatBytesM(value, 1) + '/s' }

function TxBitsTipFilter (value) { return FormatBits(value, 1) + '/s' }

function TxBitsSnsrsTipFilter (value, count) { return FormatBits(value, 1) + `/s total on ${count} sensor${count > 1 ? 's' : ''}` }

function LicenseTPTipFilter (value, count) { return FormatBits(value, 1) + `/s rolling avg on ${count} sensor${count > 1 ? 's' : ''}` }

function kBytesTipFilter (value) { return FormatBytesM(value * 1000, 1) }

// returns string with unit packets/s
function TxPktsTipFilter (value) { return value.toFixed(2) + ' p/s' }

// displays friendly alerts tooltips
function alertsTipFilter (value) { return value.toFixed(2) + ' alerts p/m' }

function Alerts2TipFilter (value) { return value.toFixed(2) + ' alerts' }

function ErrorsTipFilter (value) { return value.toFixed(2) + ' errors p/m' }

function FilesTipFilter (value) { return value.toFixed(2) + ' files p/m' }

// displays friendly logs tooltips
function logsTipFilter (value) { return value.toFixed(2) + ' logs p/s' }

function Logs2TipFilter (value) { return value.toFixed(2) + ' logs' }

// displays percent tooltips
function usedCpu (value) { return (value * 1).toFixed(2) + '%' }

// displays friendly avail memory tooltips
function availMem (value) { return parseInt(value, 10) + ' MB' }

// displays friendly load avg tooltips
function loadAvg (value) { return value.toFixed(2) + ' Load avg' }

// displays friendly code temp
function CoreTemp (value) { return value.toFixed(2) + ' °C' }

// displays percent tooltips
function percentFilter (value) { return (value * 100).toFixed(2) + '%' }

// returns string with unit operations/second = Op/s??
function opsTipFilter (value) { return value.toFixed(2) + ' Op/s' }

function BytesTipFilter (value) { return FormatBytesM(value, 1) + '/s' }

function DocsTipFilter (value) {
    return value.toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' documents';
}

function Gbps2Byps (gbps) {
    return gbps * (1000 * 1000 * 1000);
}

function IsVM (model, machineType) {
    // new approach as of 4.0.0 to use machine type
    if (machineType && machineType.length > 0) {
        return machineType.toLowerCase() !== 'physical';
    }

    // this is a very brittle approach
    // fallback for sensors older than 4.0.0
    return [
        'VMware', 'VMsmall', 'VMpacketd1', 'VMpacketd2', 'VGX-1R1',
        'VGX-1P1', 'VGX-10R1', 'VSA-S1', 'VMA-S1', 'CMCVMware'
    ].indexOf(model) >= 0;
}

const HealthService = new HealthDataService();

export {
    HealthService as default,
    IsVM,
    TxBitsTipFilter,
    BytesTipFilter,
    CoreTemp,
    Logs2TipFilter,
    FilesTipFilter,
    ErrorsTipFilter,
    Alerts2TipFilter,
    TxPktsTipFilter,
    TxBytesTipFilter,
    DocsTipFilter,
    Gbps2Byps,
    LicenseTPTipFilter,
    TxBitsSnsrsTipFilter,
};
